/**
 * Which bottles belong at serving temperature, and which shelf they belong on.
 *
 * Task `8.12`, and the one piece of physical cellar work this application can
 * initiate rather than record. Everything else here writes down something that
 * already happened; this says *go and move those three*.
 *
 * It is a rule and not a screen for the usual reason — the same one that put
 * `drinking-urgency.ts` in `shared/` — but the reader here is a single page.
 * It lives beside that file rather than inside it because it asks a different
 * question of the same answer: *Att öppna* asks what is running out, and this
 * asks which of those is standing in the wrong cabinet to do anything about
 * tonight.
 */
/**
 * The reasons that put a bottle in the fridge, of the five that put one on
 * *Att öppna*.
 *
 * **`soon` is deliberately not here.** Reading three years ahead is right for
 * a list you plan by and wrong for an act you perform this afternoon, and this
 * cellar is what settles it: of the 138 bottles in the ageing cabinet, one
 * closes this year and **31 close within three years** — against a serving
 * cabinet that holds 38 and has 38 in it. A prompt naming 32 bottles is not a
 * prompt, it is a second cabinet. Measured 2026-09-15, recorded in
 * `project-scope.md`.
 *
 * `opens` stays, though it is the one entry that is not urgency at all: a wine
 * that came into its window this year is *ready now*, which is the whole of
 * what this page is about, and the fridge is where ready wine waits.
 */
export const SERVING_URGENCIES = ["past", "closing", "peak", "opens"];
/**
 * Whether this is a bottle to carry to the serving cabinet today.
 *
 * Takes the null that `drinkingUrgency` returns, so a caller can hand over
 * whatever it got without asking a question first: a wine with no window, with
 * contradictory years, or simply years away from mattering is not work.
 */
export function wantedAtServingTemperature(urgency) {
    return urgency !== null && SERVING_URGENCIES.includes(urgency);
}
/**
 * What each kind of wine wants to be poured at, in whole degrees Celsius.
 *
 * Conventional service temperatures, and they are here to *choose between two
 * shelves* rather than to be displayed as advice — nobody needs this
 * application to tell them Champagne is served cold. That is why one figure
 * per type is enough where the books give a range: the arithmetic that reads
 * it is a comparison of distances, and the midpoint of the range answers it
 * exactly as well as the range would.
 *
 * Red at 16° is the figure that looks wrong and is not. A wine fridge's warm
 * zone is kept at about 15°, which is below the glass but above the room, and
 * a bottle poured from it arrives in the glass at the right temperature by the
 * time it is drunk. The alternative reading — that red wants 18° and therefore
 * belongs in neither zone — would put every red in this cellar on the cold
 * shelf, which is the wrong answer arrived at rigorously.
 */
export const SERVING_TEMPERATURES = {
    Champagne: 7,
    Cremant: 7,
    Sparkling: 7,
    White: 9,
    Rose: 9,
    SweetWhite: 8,
    Dessert: 8,
    Red: 16,
};
/**
 * Which shelf to propose for this wine, or null for *say nothing*.
 *
 * Null is a real answer and the honest one in three cases: there is nowhere to
 * put it, or no shelf in the cabinet records a temperature, or — which amounts
 * to the same thing — every candidate is as good as every other. A default
 * chosen with no information is a guess wearing the clothes of a suggestion,
 * and the screen asks instead.
 *
 * A cabinet with exactly one shelf is answered without consulting a
 * thermometer, because there is no choice to make and refusing to name the
 * only shelf there is would be pedantry rather than honesty.
 *
 * Ties go to the first shelf offered, which is the order the cabinet's own
 * page lists them in — so two shelves at the same temperature propose the
 * upper one, consistently, rather than whichever the sort happened to leave
 * first.
 */
export function chooseServingShelf(type, shelves) {
    const only = shelves[0];
    if (only === undefined)
        return null;
    if (shelves.length === 1)
        return only.shelfId;
    const wanted = SERVING_TEMPERATURES[type];
    let best = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const shelf of shelves) {
        if (shelf.temperatureC === null)
            continue;
        const distance = Math.abs(shelf.temperatureC - wanted);
        if (distance < bestDistance) {
            best = shelf;
            bestDistance = distance;
        }
    }
    return best?.shelfId ?? null;
}
//# sourceMappingURL=serving.js.map